import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ClientAccountEmailModel, NewClientEmailModel } from "services/api/cplapi/CPLAPI";
import { IClientPortalAccountContactDetailsService } from "services/ClientPortalAccountContactDetailsService";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { RootState } from "setup/redux/RootReducer";
import { container } from "setup/dependency/inversify.config";
import { toast } from "react-toastify";

type ClientEmailAddressesProps = {
  LoadingClientEmailAddressDetails: boolean;
  ClientEmailAddressDetails: ClientAccountEmailModel[];
  DefaultEmailAddress: ClientAccountEmailModel;
};

const initialState: ClientEmailAddressesProps = {
  LoadingClientEmailAddressDetails: false,
  ClientEmailAddressDetails: new Array<ClientAccountEmailModel>(),
  DefaultEmailAddress: new ClientAccountEmailModel({ emailAddress: "", emailDescription: "" }),
};

export const ClientEmailAddressesSlice = createSlice({
  name: "ClientEmailAddresses",
  initialState,
  reducers: {
    setDefaultEmailAddress: (state, action) => {
      state.DefaultEmailAddress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createNewClientEmail.pending, (state) => {
        state.LoadingClientEmailAddressDetails = false;
      })
      .addCase(createNewClientEmail.rejected, (state) => {
        state.LoadingClientEmailAddressDetails = false;
      })
      .addCase(createNewClientEmail.fulfilled, (state) => {
        state.LoadingClientEmailAddressDetails = false;
        toast.success("Successfully added email address");
      })
      .addCase(getClientEmailAddressDetails.pending, (state) => {
        state.LoadingClientEmailAddressDetails = true;
      })
      .addCase(getClientEmailAddressDetails.fulfilled, (state, action) => {
        state.LoadingClientEmailAddressDetails = false;
        state.ClientEmailAddressDetails = action.payload;
        state.DefaultEmailAddress =
          state.ClientEmailAddressDetails !== null && state.ClientEmailAddressDetails.length > 0
            ? state.ClientEmailAddressDetails?.find((i) => i.default) ?? state.ClientEmailAddressDetails[0]
            : new ClientAccountEmailModel();
      })
      .addCase(getClientEmailAddressDetails.rejected, (state) => {
        state.LoadingClientEmailAddressDetails = false;
      })
      .addCase(updateClientEmailAddressDetails.pending, (state) => {
        state.LoadingClientEmailAddressDetails = true;
      })
      .addCase(updateClientEmailAddressDetails.fulfilled, (state) => {
        state.LoadingClientEmailAddressDetails = false;
        toast.success("Successfully updated email address");
      })
      .addCase(updateClientEmailAddressDetails.rejected, (state) => {
        state.LoadingClientEmailAddressDetails = false;
      })
      .addCase(deleteClientEmailAddress.pending, (state) => {
        state.LoadingClientEmailAddressDetails = true;
      })
      .addCase(deleteClientEmailAddress.fulfilled, (state) => {
        state.LoadingClientEmailAddressDetails = false;
        toast.success("Successfully deleted email address");
      })
      .addCase(deleteClientEmailAddress.rejected, (state) => {
        state.LoadingClientEmailAddressDetails = false;
      });
  },
});

export const createNewClientEmail = createAsyncThunk<
  void,
  { newClientEmail: NewClientEmailModel },
  { state: RootState }
>("ClientEmailAddresses/CreateNewClientEmail", async ({ newClientEmail }, thunkAPI) => {
  const clientPortalAccountContactDetailsService = container.get<IClientPortalAccountContactDetailsService>(
    SERVICE_TYPES.ClientPortalAccountContactDetailsService
  );
  await clientPortalAccountContactDetailsService.createClientEmail(newClientEmail);
  const state = thunkAPI.getState();
  thunkAPI.dispatch(getClientEmailAddressDetails({ clientID: state.clientContactDetails.ClientID }));
});

export const getClientEmailAddressDetails = createAsyncThunk<ClientAccountEmailModel[], { clientID: string }>(
  "ClientEmailAddresses/GetClientEmailAddressDetails",
  async ({ clientID }) => {
    const clientPortalAccountContactDetailsService = container.get<IClientPortalAccountContactDetailsService>(
      SERVICE_TYPES.ClientPortalAccountContactDetailsService
    );
    return await clientPortalAccountContactDetailsService.getClientEmailAddressDetails(clientID);
  }
);

export const updateClientEmailAddressDetails = createAsyncThunk<
  void,
  { clientAccountEmailModel: ClientAccountEmailModel },
  { state: RootState }
>("ClientEmailAddresses/UpdateClientEmailAddressDetails", async ({ clientAccountEmailModel }, thunkAPI) => {
  const clientPortalAccountContactDetailsService = container.get<IClientPortalAccountContactDetailsService>(
    SERVICE_TYPES.ClientPortalAccountContactDetailsService
  );
  const state = thunkAPI.getState();
  await clientPortalAccountContactDetailsService.updateClientEmailAddress(clientAccountEmailModel);
  thunkAPI.dispatch(getClientEmailAddressDetails({ clientID: state.clientContactDetails.ClientID }));
});

export const deleteClientEmailAddress = createAsyncThunk<void, { clientAccountEmailID: string }, { state: RootState }>(
  "ClientEmailAddresses/DeleteClientEmailAddress",
  async ({ clientAccountEmailID }, thunkAPI) => {
    const clientPortalAccountContactDetailsService = container.get<IClientPortalAccountContactDetailsService>(
      SERVICE_TYPES.ClientPortalAccountContactDetailsService
    );
    const state = thunkAPI.getState();
    await clientPortalAccountContactDetailsService.deleteClientEmailAddress(clientAccountEmailID);
    thunkAPI.dispatch(getClientEmailAddressDetails({ clientID: state.clientContactDetails.ClientID }));
  }
);

export const selectLoadingClientEmailAddressDetails = (state: RootState) => {
  return state.clientEmailAddresses.LoadingClientEmailAddressDetails;
};

export const selectClientEmailAddressDetails = (state: RootState) => {
  return state.clientEmailAddresses.ClientEmailAddressDetails;
};

export const selectNewEmail = (state: RootState) => {
  const model = new NewClientEmailModel({ emailAddress: "", description: "" });
  model.clientAccountNumber = state.clientContactDetails.ClientID;
  return model;
};

export const selectDefaultEmailAddress = (state: RootState) => {
  return state.clientEmailAddresses.DefaultEmailAddress;
};

export const { setDefaultEmailAddress } = ClientEmailAddressesSlice.actions;
export default ClientEmailAddressesSlice.reducer;
